import { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import type { Transaction } from '@/lib/types';
import { useAppStore } from '@/lib/data';

interface DashboardHeroProps {
  userName: string;
  userId: string;
  avatarUrl?: string | null;
  transactions: Transaction[];
}

export function DashboardHero({ userName, avatarUrl, transactions }: DashboardHeroProps) {
  const { bills, savingsGoals, categories } = useAppStore();
  const now = new Date();

  const greeting = useMemo(() => {
    const h = now.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const firstName = userName.split(' ')[0];
  const initials = userName.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  const { income, expenses, net, savingsRate } = useMemo(() => {
    const m = now.getMonth(), y = now.getFullYear();
    const monthly = transactions.filter(t => {
      const d = new Date(t.date);
      return d.getMonth() === m && d.getFullYear() === y;
    });
    const income = monthly.filter(t => t.type === 'income').reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const expenses = monthly.filter(t => t.type === 'expense').reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const net = income - expenses;
    const savingsRate = income > 0 ? Math.max(0, Math.round(((income - expenses) / income) * 100)) : 0;
    return { income, expenses, net, savingsRate };
  }, [transactions]);

  // Contextual insight â€” changes based on actual data
  const insight = useMemo(() => {
    const overdueBills = bills.filter(b => b.status === 'unpaid' && new Date(b.dueDate) < now).length;
    const dueSoon = bills.filter(b => {
      const d = new Date(b.dueDate);
      return b.status === 'unpaid' && d >= now && d <= new Date(now.getTime() + 7 * 86400000);
    }).length;

    const budgetCats = categories.filter(c => c.type === 'expense' && c.budget && c.workspace === 'personal');
    const overBudget = budgetCats.filter(c => {
      const spent = transactions
        .filter(t => t.category_id === c.id || t.category === c.name)
        .reduce((s, t) => s + Math.abs(t.amount), 0);
      return spent > (c.budget || 0);
    }).length;

    if (overdueBills > 0) return { text: `${overdueBills} overdue bill${overdueBills > 1 ? 's' : ''} need attention`, urgent: true };
    if (dueSoon > 0) return { text: `${dueSoon} bill${dueSoon > 1 ? 's' : ''} due this week`, urgent: false };
    if (overBudget > 0) return { text: `${overBudget} budget categor${overBudget > 1 ? 'ies' : 'y'} over limit`, urgent: true };
    if (savingsRate >= 20) return { text: `Saving ${savingsRate}% of income â€” great work!`, urgent: false };
    if (transactions.length === 0) return { text: 'Add your first transaction to get started', urgent: false };
    return { text: `${transactions.length} transaction${transactions.length !== 1 ? 's' : ''} this month`, urgent: false };
  }, [bills, categories, transactions, savingsRate]);

  const NetIcon = net > 0 ? TrendingUp : net < 0 ? TrendingDown : Minus;
  const netColor = net > 0 ? 'text-emerald-500' : net < 0 ? 'text-rose-500' : 'text-slate-400';

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8 pb-2">

      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-2 ring-offset-2 ring-indigo-500 overflow-hidden bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
          {avatarUrl
            ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" />
            : <span className="text-2xl font-bold text-white">{initials}</span>
          }
        </div>
        <span className="absolute bottom-1 right-1 w-3.5 h-3.5 bg-emerald-400 border-2 border-white rounded-full" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 mb-0.5">
          <h1 className="text-xl font-bold text-slate-900 truncate">{userName}</h1>
          <span className="text-sm text-slate-400 font-medium">{dateStr}</span>
        </div>

        <p className="text-sm text-slate-500 mb-3">
          {greeting}, <span className="font-semibold text-slate-700">{firstName}</span> 👋
          <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${insight.urgent ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>
            {insight.text}
          </span>
        </p>

        {/* Stats â€” truncate currency on mobile */}
        <div className="flex items-center gap-4 sm:gap-8 overflow-x-auto scrollbar-hide">
          <div className="text-center flex-shrink-0">
            <p className="text-sm sm:text-base font-bold text-slate-900 tabular-nums">{formatCurrency(income)}</p>
            <p className="text-xs text-slate-400 font-medium mt-0.5">Income</p>
          </div>
          <div className="w-px h-8 bg-slate-200 flex-shrink-0" />
          <div className="text-center flex-shrink-0">
            <p className="text-sm sm:text-base font-bold text-slate-900 tabular-nums">{formatCurrency(expenses)}</p>
            <p className="text-xs text-slate-400 font-medium mt-0.5">Spent</p>
          </div>
          <div className="w-px h-8 bg-slate-200 flex-shrink-0" />
          <div className="text-center flex-shrink-0">
            <div className="flex items-center justify-center gap-1">
              <NetIcon className={`w-3.5 h-3.5 flex-shrink-0 ${netColor}`} strokeWidth={2.5} />
              <p className={`text-sm sm:text-base font-bold tabular-nums ${netColor}`}>{formatCurrency(Math.abs(net))}</p>
            </div>
            <p className="text-xs text-slate-400 font-medium mt-0.5">Net</p>
          </div>
          <div className="w-px h-8 bg-slate-200 flex-shrink-0" />
          <div className="text-center flex-shrink-0">
            <p className={`text-sm sm:text-base font-bold tabular-nums ${savingsRate >= 20 ? 'text-emerald-600' : savingsRate > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
              {savingsRate}%
            </p>
            <p className="text-xs text-slate-400 font-medium mt-0.5">Saved</p>
          </div>
        </div>
      </div>
    </div>
  );
}
